import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { signOut } from "@aws-amplify/auth";
import { BehaviorSubject, from, Observable, Subject } from "rxjs";
import { distinctUntilChanged, tap } from "rxjs/operators";
import { UnsavedChangesService } from "src/app/services/unsaved-changes.service";
import { environment } from "src/environments/environment";
import { BucketService } from "src/app/shared/services/bucket.service";
import { CreatorAnalyticsService } from "src/app/shared/services/creator-analytics.service";

export type BackendCreatorSortKey =
  | "creator"
  | "followers"
  | "engagement"
  | "avg_likes"
  | "location"
  | "gender"
  | "contact_info";

export interface CreditsStatusPayload {
  validity_start?: string;
  validity_end?: string;
  credits_available?: number;
  credits_plan_total?: number;
  credits_used_company?: number;
  credits_used_user?: number;
  subscription?: {
    event?: string;
    plan_transaction_id?: string;
  };
  trial?: {
    days_remaining?: number[];
  };
}

export interface SubscriptionLifecycleEvent {
  event: "created" | "renewed" | "paid" | "canceled" | "past_due" | "unknown";
  planTransactionId: string | null;
  raw: any;
}

export interface TrialState {
  active: boolean;
  daysRemaining: number | null;
  thresholds: number[];
  lastBroadcastAt: string | null;
}

@Injectable({
  providedIn: "root",
})
export class PagesService {
  private readonly languageHeaders = new HttpHeaders({
    "Accept-Language": "en-US,en",
  });
  currentPage$ = new BehaviorSubject<string>("");
  private socket: WebSocket;
  private readonly USER_STORAGE_KEY = "user";
  private userSubject = new BehaviorSubject<any>(null);
  totalCredits$ = new BehaviorSubject<number>(0);
  availableCredits$ = new BehaviorSubject<number>(0);
  private subscriptionEventsSubject = new Subject<SubscriptionLifecycleEvent>();
  subscriptionEvents$ = this.subscriptionEventsSubject.asObservable();
  private trialStateSubject = new BehaviorSubject<TrialState>({
    active: false,
    daysRemaining: null,
    thresholds: [],
    lastBroadcastAt: null,
  });
  trialState$ = this.trialStateSubject.asObservable();
  private pastDueSubject = new BehaviorSubject<boolean>(false);
  pastDue$ = this.pastDueSubject.asObservable();
  private billingRefreshSubject = new Subject<CreditsStatusPayload>();
  billingRefresh$ = this.billingRefreshSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private uss: UnsavedChangesService,
    private bucketService: BucketService,
    private creatorAnalyticsService: CreatorAnalyticsService
  ) {
    const storedUser = this.getStoredUser();
    if (storedUser) {
      this.userSubject.next(storedUser);
    }
  }

  initPaymentWs() {
    return this.httpClient.get(`${environment.apiUrl}api/common/ws/`);
  }

  connectPaymentWs() {
    if (!this.socket) {
      this.socket = new WebSocket(
        `${environment.websocketUrl}ws/notifications/${
          JSON.parse(localStorage.getItem("user")).id
        }/`
      );
    }
    return new Observable<any>((observer) => {
      this.socket.onopen = () => {
        console.log("[WebSocket] Connected");
      };

      this.socket.onmessage = (event: MessageEvent) => {
        const data = JSON.parse(event.data);
        observer.next(data);
      };

      this.socket.onerror = (error) => {
        console.error("[WebSocket] Error:", error);
        observer.error(error);
      };

      this.socket.onclose = (event) => {
        console.log("[WebSocket] Closed:", event.reason);
        observer.complete();
      };

      return () => {
        if (this.socket.readyState === WebSocket.OPEN) {
          this.socket.close(1000, "Closed by client unsubscribe");
        }
      };
    });
  }

  disconnectPaymentWs() {
    this.socket = null;
    this.socket?.close();
  }

  applyRealtimeCreditsUpdate(payload: CreditsStatusPayload): void {
    if (!payload || typeof payload !== "object") {
      return;
    }

    const basePayload: any = payload;
    const creditsBlock =
      basePayload && typeof basePayload.status === "object"
        ? basePayload.status
        : basePayload;

    if (typeof creditsBlock.credits_plan_total === "number") {
      this.totalCredits = creditsBlock.credits_plan_total;
    }
    if (typeof creditsBlock.credits_available === "number") {
      this.availableCredits = creditsBlock.credits_available;
    }

    const subscriptionBlock =
      basePayload.subscription ?? creditsBlock.subscription ?? null;
    if (subscriptionBlock) {
      const lifecycleEvent = (
        subscriptionBlock.event || "unknown"
      ).toLowerCase();
      const normalized: SubscriptionLifecycleEvent = {
        event: ["created", "renewed", "paid", "canceled", "past_due"].includes(
          lifecycleEvent
        )
          ? (lifecycleEvent as SubscriptionLifecycleEvent["event"])
          : "unknown",
        planTransactionId: subscriptionBlock.plan_transaction_id ?? null,
        raw: subscriptionBlock,
      };
      this.subscriptionEventsSubject.next(normalized);
      this.pastDueSubject.next(normalized.event === "past_due");
      this.billingRefreshSubject.next({
        ...creditsBlock,
        subscription: subscriptionBlock,
      });
    }

    const trialBlock = basePayload.trial ?? creditsBlock.trial ?? null;
    if (trialBlock) {
      const days = Array.isArray(trialBlock.days_remaining)
        ? [...trialBlock.days_remaining].sort((a, b) => a - b)
        : [];
      const state: TrialState = {
        active: days.length > 0,
        daysRemaining: days.length > 0 ? days[0] : null,
        thresholds: days,
        lastBroadcastAt: new Date().toISOString(),
      };
      this.trialStateSubject.next(state);
      if (days.length > 0) {
        this.billingRefreshSubject.next({
          ...creditsBlock,
          trial: trialBlock,
        });
      }
    } else if (this.trialStateSubject.value.active) {
      this.trialStateSubject.next({
        active: false,
        daysRemaining: null,
        thresholds: [],
        lastBroadcastAt: new Date().toISOString(),
      });
    }
  }

  userInfo() {
    return this.httpClient.get(`${environment.apiUrl}api/accounts/me/`);
  }

  // Set the entire user object
  setUser(user: any): void {
    localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(user));
    this.userSubject.next(user);
  }

  // Watch for user changes
  getUserObservable(): Observable<any> {
    return this.userSubject
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        )
      );
  }

  private getStoredUser(): any {
    const userJson = localStorage.getItem(this.USER_STORAGE_KEY);
    return userJson ? JSON.parse(userJson) : null;
  }

  get currentPage(): Observable<string> {
    return this.currentPage$.asObservable();
  }

  set currentPage(page: string) {
    this.currentPage$.next(page);
  }

  logout(token: string) {
    return from(signOut());
  }

  startCheckoutSession(planId: string, recurlyToken?: string) {
    let params = new HttpParams().set("plan_id", planId);
    if (recurlyToken) {
      params = params.set("recurly_token", recurlyToken);
    }
    return this.httpClient.get(
      `${environment.apiUrl}api/clients/payments/session/checkout/`,
      { params, headers: this.languageHeaders }
    );
  }

  getPlans(
    params?: Record<
      string,
      string | number | boolean | ReadonlyArray<string | number | boolean>
    >
  ) {
    return this.httpClient.get(`${environment.apiUrl}api/clients/plan/`, {
      params,
      headers: this.languageHeaders,
    });
  }

  getPlansFrontend(
    params?: Record<
      string,
      string | number | boolean | ReadonlyArray<string | number | boolean>
    >
  ) {
    return this.httpClient.get(
      `${environment.apiUrl}api/clients/plan/frontend/`,
      { params, headers: this.languageHeaders }
    );
  }

  getPaymentsProvider() {
    return this.httpClient.get(
      `${environment.apiUrl}api/clients/public/provider/`,
      { headers: this.languageHeaders }
    );
  }

  /**
   * Get Pendo configuration from backend.
   * Returns { enabled: boolean, app_key?: string }
   */
  getPendoConfig() {
    return this.httpClient.get<{ enabled: boolean; app_key?: string }>(
      `${environment.apiUrl}api/clients/public/pendo/`,
      { headers: this.languageHeaders }
    );
  }

  getPricingSessions() {
    return this.httpClient.get(
      `${environment.apiUrl}api/clients/payments/session/pricingtable`,
      { headers: this.languageHeaders }
    );
  }

  getPortalSessions() {
    return this.httpClient.get(
      `${environment.apiUrl}api/clients/payments/session/portal`,
      { headers: this.languageHeaders }
    );
  }

  cancelSubscription(planTransactionId: string) {
    return this.httpClient.post(
      `${environment.apiUrl}api/clients/payments/subscription/cancel/`,
      { plan_transaction_id: planTransactionId },
      { headers: this.languageHeaders }
    );
  }

  get isManager() {
    return JSON.parse(localStorage.getItem("user")).is_manager;
  }

  saveUserDetails(payload) {
    return this.httpClient.post(
      `${environment.apiUrl}api/accounts/update-me/`,
      payload
    );
  }

  getDashboardData() {
    return this.httpClient.get(`${environment.apiUrl}api/clients/dashboard/`);
  }

  /**
   * Searches for creators based on the given payload for existing Id
   * @param payload The search request payload
   * @returns The search response object
   */
  creatorsFromSearchId(
    payload: any,
    pagination?: any,
    sort?: BackendCreatorSortKey,
    order?: "asc" | "desc",
    // CAT-1189: ask for the context block only — no creator rows. The campaign
    // detail page renders its header and its Dashboard, Settings, Brief and
    // Personas tabs from `metadata.context` alone, and paid a full
    // 20,948-creator container read for it: measured at 9,850ms of a 10,215ms
    // response. Off by default; only the detail page opts in, because
    // creator-cards calls this for the rows it actually shows.
    metaOnly: boolean = false
  ) {
    let url = `${environment.apiUrl}api/aitools/results/${payload}/?page=${pagination?.page}`;
    if (sort) {
      url += `&sort=${sort}&order=${order}`;
    }
    if (metaOnly) {
      url += `&meta_only=true`;
    }
    return this.httpClient.get(url);
  }

  // =========================================================================
  // CAT-802: V2 API Methods for Load More Feature
  // =========================================================================

  /**
   * Execute initial search with a specific limit using V2 API.
   * This is used when loadMoreEnabled feature flag is active.
   * @param searchType Type of search: 'list_search', 'campaign', or 'ai_campaign'
   * @param searchId The search ID (ListSearch, Campaign, or AICampaign)
   * @param initialLimit Number of results to fetch initially
   * @returns Observable with search results
   */
  executeSearchV2(searchType: 'list_search' | 'campaign' | 'ai_campaign', searchId: string, initialLimit: number) {
    const typeToEndpoint: Record<string, string> = {
      'list_search': 'list-search',
      'campaign': 'campaign',
      'ai_campaign': 'ai-campaign'
    };
    const endpoint = typeToEndpoint[searchType] || 'list-search';
    const url = `${environment.apiUrl}api/aitools/v2/${endpoint}/${searchId}/execute/`;
    const body = { initial_limit: initialLimit };
    return this.httpClient.post(url, body);
  }

  /**
   * Load more results using V2 API with cursor-based pagination.
   * @param searchType Type of search: 'list_search', 'campaign', or 'ai_campaign'
   * @param searchId The search ID
   * @param batchSize Number of additional results to load (or "all")
   * @param cursor Pagination cursor from previous execute/load-more response
   * @returns Observable with additional results
   */
  loadMoreV2(
    searchType: 'list_search' | 'campaign' | 'ai_campaign',
    searchId: string,
    batchSize: number | string,
    cursor: string,
  ) {
    const typeToEndpoint: Record<string, string> = {
      'list_search': 'list-search',
      'campaign': 'campaign',
      'ai_campaign': 'ai-campaign'
    };
    const endpoint = typeToEndpoint[searchType] || 'list-search';
    return this.httpClient.post(
      `${environment.apiUrl}api/aitools/v2/${endpoint}/${searchId}/load-more/`,
      { batch_size: String(batchSize), cursor }
    );
  }

  /**
   * Get preview of credits needed for loading more results.
   * @param searchType Type of search: 'list_search', 'campaign', or 'ai_campaign'
   * @param searchId The search ID
   * @returns Observable with credit preview information
   */
  loadMorePreviewV2(searchType: 'list_search' | 'campaign' | 'ai_campaign', searchId: string) {
    const typeToEndpoint: Record<string, string> = {
      'list_search': 'list-search',
      'campaign': 'campaign',
      'ai_campaign': 'ai-campaign'
    };
    const endpoint = typeToEndpoint[searchType] || 'list-search';
    return this.httpClient.get(
      `${environment.apiUrl}api/aitools/v2/${endpoint}/${searchId}/load-more/preview/`
    );
  }

  /**
   * Get current V2 search status from the backend (source of truth).
   * Used on re-entry to restore Load More state without consuming credits.
   * @param searchType Type of search: 'list_search', 'campaign', or 'ai_campaign'
   * @param searchId The CreatorSearch UUID
   * @returns Observable with V2 state (estimated_total, total_fetched, cursor, etc.)
   */
  getSearchStatusV2(searchType: 'list_search' | 'campaign' | 'ai_campaign', searchId: string) {
    const typeToEndpoint: Record<string, string> = {
      'list_search': 'list-search',
      'campaign': 'campaign',
      'ai_campaign': 'ai-campaign'
    };
    const endpoint = typeToEndpoint[searchType] || 'list-search';
    return this.httpClient.get(
      `${environment.apiUrl}api/aitools/v2/${endpoint}/${searchId}/status/`
    );
  }

  actionOnCreators(payload: any) {
    return this.bucketService.addBucketMember(payload);
  }

  addCreatorToResults(resultUuid: string, payload: any) {
    return this.httpClient.post(
      `${environment.apiUrl}api/aitools/results/${resultUuid}/add-creators/`,
      payload
    );
  }

  actionOnCampaignBucket(id: string) {
    return this.bucketService.removeBucketMember(id);
  }

  getCreatorsCsv(id: string, payload: {}) {
    return this.httpClient.post(
      `${environment.apiUrl}api/clients/exportresults/${id}/create_csv/`,
      payload,
      { responseType: "arraybuffer" }
    );
  }

  set totalCredits(credits: number) {
    this.totalCredits$.next(credits);
  }

  get updatedTotalCredits(): Observable<number> {
    return this.totalCredits$.asObservable();
  }

  set availableCredits(credits: number) {
    this.availableCredits$.next(credits);
  }

  get updatedAvailableCredits(): Observable<number> {
    return this.availableCredits$.asObservable();
  }

  getUpdatedCredits() {
    return this.httpClient.get(
      `${environment.apiUrl}api/clients/credits/status`,
      { headers: this.languageHeaders }
    );
  }

  refreshCreditsSnapshot(): Observable<any> {
    return this.getUpdatedCredits().pipe(
      tap((credits: any) => {
        this.totalCredits = credits?.["credits_plan_total"] ?? 0;
        this.availableCredits = credits?.["credits_available"] ?? 0;
      })
    );
  }

  handleUnsavedChanges(form: FormGroup | null) {
    this.uss.init(form);
  }

  /**
   * Check if trial notification should be shown to the user
   * @returns Observable with notification status
   */
  getTrialNotificationStatus(): Observable<any> {
    return this.httpClient.get(
      `${environment.apiUrl}api/clients/trial/status/`,
      { headers: this.languageHeaders }
    );
  }

  /**
   * Mark trial notification as viewed by the user
   * @param action The action taken: 'dismissed' or 'add_payment'
   * @returns Observable with response
   */
  markTrialNotificationViewed(
    action: "dismissed" | "add_payment"
  ): Observable<any> {
    return this.httpClient.post(
      `${environment.apiUrl}api/clients/trial/notification/viewed/`,
      { action },
      { headers: this.languageHeaders }
    );
  }

  /**
   * Reactivate a canceled subscription (before it expires)
   * @param planTransactionId The subscription ID to reactivate
   * @returns Observable with response
   */
  reactivateSubscription(planTransactionId: string): Observable<any> {
    return this.httpClient.post(
      `${environment.apiUrl}api/clients/payments/subscription/reactivate/`,
      { plan_transaction_id: planTransactionId },
      { headers: this.languageHeaders }
    );
  }

  /**
   * Convert a trial subscription to immediate paid subscription
   * @param planTransactionId The subscription ID to convert
   * @returns Observable with response
   */
  convertTrialToPaid(planTransactionId: string): Observable<any> {
    return this.httpClient.post(
      `${environment.apiUrl}api/clients/payments/subscription/convert-trial/`,
      { plan_transaction_id: planTransactionId },
      { headers: this.languageHeaders }
    );
  }

  /**
   * Convert a trial subscription to immediate paid subscription with billing token
   * @param planTransactionId The subscription ID to convert
   * @param recurlyToken The Recurly token from Elements
   * @returns Observable with response
   */
  convertTrialToPaidWithToken(
    planTransactionId: string,
    recurlyToken: string
  ): Observable<any> {
    return this.httpClient.post(
      `${environment.apiUrl}api/clients/payments/subscription/convert-trial/`,
      {
        plan_transaction_id: planTransactionId,
        recurly_token: recurlyToken,
      },
      { headers: this.languageHeaders }
    );
  }

  /**
   * Check Profile Analytics cache and credit status
   * @param platformId The profile platform ID
   * @param identifier The username
   * @returns Observable with response
   */
  checkProfileAnalyticsCache(
    platformId: string,
    identifier: string
  ): Observable<any> {
    return this.creatorAnalyticsService.checkAnalyticsCache(platformId, identifier);
  }

  /**
   * Fetch Report Form Schema
   */
  getReportFormSchema(): Observable<any> {
    return this.httpClient.get(
      `${environment.apiUrl}api/feedback/form/bug-report-feedback`
    );
  }

  submitReportFormData(payload: any): Observable<any> {
    return this.httpClient.post(
      `${environment.apiUrl}api/feedback/form/bug-report-feedback/submit`,
      payload
    );
  }
}
